/** @jsxImportSource @emotion/react */
import { memo, useState, useEffect, useRef, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import ReactLoading from 'react-loading';
 
import { mtgGroup, mtgLabel } from "assets/css/stylesCss";
import { loadingCss } from "assets/css/shchMtmr";

import InputTextHalf from "components/ui/input/InputTextHalf";
import CommonPulldown from "components/ui/pulldown/CommonPulldown";
import ValidationErrorText from "components/ui/validation/ValidationErrorText";

import useGetLazyQuery from "hooks/useGetLazyQuery";
import { GET_ADDRESS } from "graphql/query";

/**
 * 郵便番号 + 都道府県・市区町村・丁目
 * @param {String} yubinNo 姓 react-hook-form管理用フォーム特定文字列
 * @param {String} addressKj 名 react-hook-form管理用フォーム特定文字列
 */
const YubinNoAddressKj = memo(({ yubinNo, addressKj }) => {

  // 画面遷移用
  const navigate = useNavigate();

  const {
    getValues,
  } = useFormContext();

  const [selectTxt, setSelectTxt] = useState("選択してください");
  const [options, setOptions] = useState([{ value: "", label: "" }]);
  const [errMsgYubinNo, setErrMsgYubinNo] = useState("");

  // 郵便番号から住所一覧を取得するフック
  const [getAddress, loading] = useGetLazyQuery(GET_ADDRESS);

  const refFirstRef = useRef(true);

  // 初期表示・再描画時
  useEffect(() => {

    if (process.env.NODE_ENV === "development") {
      if (refFirstRef.current) {
        refFirstRef.current = false;
        return;
      }
    }

    (async () => {

      if (getValues(yubinNo)) {
        // オプション初期化
        setOptions(() => [{ value: "", label: "" }]);

        // 住所情報を生成
        await setAddressKj();
      }
    })()

  }, []);

  // 選択肢が変更された場合の選択表示を変更
  useEffect(() => {
    if (options[0].value === "") {
      setSelectTxt("郵便番号を入力してください");
    }
  }, [options]);

  // 都道府県・市区町村・丁目を設定
  const setAddressKj = useCallback(async () => {

    // オプション初期化
    setOptions(() => [{ value: "", label: "" }]);

    const paramYbinNo = getValues(yubinNo);

    // 郵便番号が正常に入力されていない場合、処理終了
    if (!paramYbinNo || paramYbinNo.length !== 7 || !paramYbinNo.match(/\d{7}$/)) {
      return;
    }

    // 郵便番号エラーメッセージクリア
    setErrMsgYubinNo("")

    // 郵便番号から住所一覧を取得
    const param = { input: { yubinNo: paramYbinNo } };
    await getAddress(param,
      (data) => {
        // エラー存在チェック
        let err = data.errorResponse;

        // 住所一覧を正常に取得出来た場合
        if (!err) {
          // 入力補助テキスト
          let addressOption = data.addressCdOptions;
          // 検索結果をセレクトボックス生成用optionに設定
          setSelectTxt("選択してください");
          setOptions(addressOption);
        } else {
          // 対象となる住所情報が存在しません。
          setErrMsgYubinNo(err.errorMsg)
        }
      },
      // エラー発生時
      (e) => {
        console.log("失敗…" + e)
        navigate("/error", {
          state: { errorCode: 500 },
        });
      }
    );
  }, []);

  return (
    <>
      <div css={mtgGroup}>
        <label css={mtgLabel}>郵便番号</label>
        <InputTextHalf
          items={[
            {
              name: yubinNo,
              placeholder: "1300012",
              maxLength: "7",
              inputMode: "numeric",
              rules: {
                required: {
                  value: true,
                  message: "郵便番号を入力してください。",
                },
                pattern: {
                  value: /\d{7}$/,
                  message: "郵便番号は半角数字7桁で入力してください。",
                },
              },
            },
          ]}
          isYubinNo={true}
          setAddressKj={setAddressKj}
        ></InputTextHalf>
        <ValidationErrorText>{errMsgYubinNo}</ValidationErrorText>
      </div>
      <div css={mtgGroup}>
        {/* 都道府県・市区町村・丁目 プルダウン */}
        <label css={mtgLabel}>都道府県・市区町村・丁目</label>
        <CommonPulldown
          isDisabled={options[0].value === "" ? true : false}
          name={addressKj}
          rules={{
            required: "都道府県・市区町村・丁目を入力してください。",
          }}
          placeholder={selectTxt}
          options={options}
        />
      </div>

      {/** ローディング */}
      {loading && (
        <div css={[loadingCss]}>
          <ReactLoading type="spin" color="#b5b5b5" height="50px" width="50px" />
        </div>
      )}
    </>
  );
});

export default YubinNoAddressKj;
